import React from 'react';
import styled from 'styled-components';
import Button from '@/components/core/Button';
import { useTranslations } from 'next-intl';
import { FlexCol3XL, FlexCol3XS, FlexCol4XS } from '@/components/Flex';
import Typography from '@/components/core/Typography';
import { GreyTypography, MoonOneLogo } from '@/app/[locale]/(welcome)/_components';
import { device, HideAbove, HideBelow } from '@/components/Media';
import { HelperText } from '@/app/[locale]/(welcome)/account-setup/HelperText';

const Overlay = styled.div<{
    shown: boolean;
}>`
    position: absolute;
    z-index: 4;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(12px);
    background-color: rgba(43, 82, 131, 0.40);
    opacity: ${({ shown }) => shown ? '1' : '0'};
    visibility: ${({ shown }) => shown ? 'visible' : 'hidden'};
    transition: opacity 300ms ease;

    @media screen and ${device.laptopS} {
        backdrop-filter: unset;
    }
`;

const Container = styled.div`
    background-color: ${({ theme }) => theme.colors.palette.white};
    border-radius: 12px;
    box-shadow: 0 3.5px 5.5px 0 rgba(0, 0, 0, 0.02);
    display: flex;
    width: 613px;
    padding: 54px 0;
    flex-direction: column;
    text-align: center;
    align-items: center;
    gap: 30px;

    @media screen and ${device.laptopS} {
        width: 100%;
        height: 100%;
        border-radius: 0;
        padding: 24px 12px;
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing['2xs']};
    }
`;

const ContentWrapper = styled(FlexCol3XL)`
    @media screen and ${device.laptopS} {
        width: 100%;
        gap: ${({ theme }) => theme.spacing['2xs']};
    }
`;

const DarkTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.grey_800};
`;

const ProceedButton = styled(Button)`
    justify-content: center;
    
    @media screen and ${device.laptopS} {
        width: 100%;
    }
`;

export function SetupSuccessful({ shown, onProceed }: { shown: boolean, onProceed: () => void }) {
    const t = useTranslations('welcome');

    return (
        <Overlay shown={shown}>
            <Container>
                <HideAbove md>
                    <MoonOneLogo />
                </HideAbove>

                <ContentWrapper>
                    <FlexCol3XS>
                        <HideBelow md>
                            <DarkTypography variant='uiType' size='3xl' modifier='S'>{t('success.title')}</DarkTypography>
                        </HideBelow>
                        <HideAbove md>
                            <DarkTypography variant='uiType' size='xl' modifier='S'>{t('success.title')}</DarkTypography>
                        </HideAbove>
                        <GreyTypography variant='uiType' size='xl' modifier='R'>{t('success.subtitle')}</GreyTypography>
                    </FlexCol3XS>

                    <FlexCol4XS>
                        <GreyTypography variant='uiType' size='md' modifier='S'>{t('success.text')}</GreyTypography>
                        <GreyTypography variant='uiType' size='md' modifier='S'>{t('success.subtext')}</GreyTypography>
                    </FlexCol4XS>

                    <ProceedButton
                      type='submit'
                      variant='filled'
                      size='lg'
                      onClick={onProceed}
                      innerText={t('proceed')}
                    />
                </ContentWrapper>

                <HideAbove md>
                    <HelperText />
                </HideAbove>
            </Container>
        </Overlay>
    );
}
